import { CloseOutlined, LoadingOutlined } from '@ant-design/icons';
import { MAX_UPLOAD_BYTES, MAX_UPLOAD_MB } from '@cult/domain';
import type { AttachmentInput } from '@cult/schemas';
import { App as AntApp, Button, Flex, Typography } from 'antd';
import clsx from 'clsx';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { ApiError } from '../../../../lib/api-client';
import { uploadFile } from '../../hooks';
import { formatSize, iconFor, isPreviewableImage } from '../../utils';
import { AttachmentsDropzone } from '../AttachmentsDropzone/AttachmentsDropzone';
import styles from './AttachmentsField.module.scss';
import { isAccepted, renamePasted } from './attachments-field.model';

const { Text } = Typography;

const MAX_ATTACHMENTS = 10;
const ACCEPT = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
].join(',');

const DEFAULT_HINT = `Up to 10 files · ${MAX_UPLOAD_MB} MB each · PNG, JPEG, WEBP, GIF, PDF, DOC, DOCX, XLS, XLSX`;
const tooLargeMessage = (name: string) => `${name}: File is too large. Max size is ${MAX_UPLOAD_MB} MB.`;

type Props = {
  value?: AttachmentInput[];
  onChange?: (next: AttachmentInput[]) => void;
  disabled?: boolean;
  max?: number;
  accept?: string;
  hint?: ReactNode;
  label?: string;
};

export const AttachmentsField = ({
  value = [],
  onChange,
  disabled,
  max = MAX_ATTACHMENTS,
  accept = ACCEPT,
  hint = DEFAULT_HINT,
  label = 'Attach a file',
}: Props) => {
  const { message } = AntApp.useApp();
  const inputRef = useRef<HTMLInputElement>(null);
  const [staging, setStaging] = useState<string[]>([]);
  // Keep a ref to the latest value so concurrent upload callbacks don't
  // overwrite each other (stale closure problem when selecting multiple files).
  const valueRef = useRef(value);
  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  const limitReached = value.length + staging.length >= max;

  const openPicker = () => {
    if (disabled || limitReached) return;
    inputRef.current?.click();
  };

  const addFiles = (picked: File[]) => {
    // Reject oversized files before uploading them; the API would answer 413 anyway.
    const oversized = picked.filter((file) => file.size > MAX_UPLOAD_BYTES);
    for (const file of oversized) message.error(tooLargeMessage(file.name));
    const files = picked.filter((file) => file.size <= MAX_UPLOAD_BYTES);

    const room = max - value.length - staging.length;
    const accepted = files.slice(0, room);
    if (accepted.length < files.length) {
      message.warning(`Only ${max} attachment${max === 1 ? '' : 's'} allowed; extra files were skipped.`);
    }
    for (const file of accepted) {
      const stageId = `${Date.now()}-${Math.random()}`;
      setStaging((prev) => [...prev, stageId]);
      uploadFile(file)
        .then(({ url, mime, size }) => {
          // Prefer the server's recorded mime/size; fall back to the browser's while an older API
          // (without them in the response) is still running during a deploy.
          const next = [
            ...valueRef.current,
            {
              url,
              filename: file.name,
              mime: mime ?? (file.type || 'application/octet-stream'),
              size: size ?? file.size,
            },
          ];
          valueRef.current = next;
          onChange?.(next);
          setStaging((prev) => prev.filter((id) => id !== stageId));
        })
        .catch((err: Error) => {
          const text =
            err instanceof ApiError && err.status === 413 ? tooLargeMessage(file.name) : `${file.name}: ${err.message}`;
          message.error(text);
          setStaging((prev) => prev.filter((id) => id !== stageId));
        });
    }
  };

  const onDropped = (files: File[]) => {
    if (limitReached) return;
    const supported = files.filter((file) => isAccepted(file, accept));
    if (supported.length < files.length) message.warning('Some files were skipped: unsupported file type.');
    if (supported.length > 0) addFiles(supported);
  };

  const onPicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    e.target.value = '';
    if (files.length > 0) addFiles(files);
  };

  // The document-level listener is registered once; it reads the latest handler through this ref.
  const onPasteRef = useRef<(e: ClipboardEvent) => void>(() => {});
  onPasteRef.current = (e) => {
    const files = Array.from(e.clipboardData?.files ?? []).filter((file) => isAccepted(file, accept));
    if (files.length === 0) return;
    e.preventDefault();
    if (disabled || limitReached) return;
    addFiles(files.map(renamePasted));
  };

  useEffect(() => {
    const listener = (e: ClipboardEvent) => onPasteRef.current(e);
    document.addEventListener('paste', listener);
    return () => document.removeEventListener('paste', listener);
  }, []);

  const removeAt = (idx: number) => {
    if (disabled) return;
    const next = (value ?? []).filter((_, i) => i !== idx);
    onChange?.(next);
  };

  return (
    <div className={styles.field}>
      <Flex vertical gap={8}>
        {value.length > 0 || staging.length > 0 ? (
          <ul className={styles.list}>
            {value.map((att, idx) => (
              <li key={`${att.url}-${idx}`} className={styles.chip}>
                {isPreviewableImage(att) ? (
                  <a href={att.url} target="_blank" rel="noopener noreferrer" aria-label={`Open ${att.filename}`}>
                    <img src={att.url} alt="" className={styles.thumb} />
                  </a>
                ) : (
                  <span className={styles.thumb}>{iconFor(att.mime)}</span>
                )}
                <span className={styles.chipName} title={att.filename}>
                  {att.filename}
                </span>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {formatSize(att.size)}
                </Text>
                <Button
                  size="small"
                  type="text"
                  icon={<CloseOutlined />}
                  aria-label={`Remove ${att.filename}`}
                  onClick={() => removeAt(idx)}
                  disabled={disabled}
                />
              </li>
            ))}
            {staging.map((id) => (
              <li key={id} className={clsx(styles.chip, styles.chipLoading)}>
                <span className={styles.thumb}>
                  <LoadingOutlined />
                </span>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  Uploading…
                </Text>
              </li>
            ))}
          </ul>
        ) : null}
        <AttachmentsDropzone
          title={value.length + staging.length > 0 ? 'Add another file' : label}
          hint={hint}
          disabled={disabled || limitReached}
          onOpen={openPicker}
          onDrop={onDropped}
        />
      </Flex>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple
        onChange={onPicked}
        style={{ display: 'none' }}
        aria-hidden
        tabIndex={-1}
      />
    </div>
  );
};
